import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import GlitchText from "./GlitchText";
import { HERO_CITY, LOGO } from "./assets";
import { useLang } from "./LanguageContext";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t } = useLang();
  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_CITY} alt="Futuristic island city" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#060A14]/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060A14] via-[#060A14]/40 to-[#060A14]/70" />
        {/* small faint logo watermark */}
        <img
          src={LOGO}
          alt=""
          aria-hidden="true"
          className="absolute right-[8%] top-1/2 -translate-y-1/2 w-[28%] max-w-sm opacity-[0.06] object-contain pointer-events-none select-none hidden md:block"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease }}
          className="text-[#00F2FE] text-xs sm:text-sm font-bold tracking-[0.3em] mb-7"
          data-testid="hero-eyebrow"
        >
          {t("hero.eyebrow")}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.05 }}
          className="font-display font-black uppercase leading-[0.9] text-5xl sm:text-7xl lg:text-[8rem]"
          data-testid="hero-title"
        >
          <GlitchText className="glitch-title text-[#F0F8FF] drop-shadow-[0_0_25px_rgba(0,242,254,0.35)]">INO</GlitchText>
          <br />
          <GlitchText className="glitch-title text-[#00F2FE] drop-shadow-[0_0_30px_rgba(0,242,254,0.55)]">ELITES</GlitchText>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.25 }}
          className="mt-9 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.4 }}
          className="mt-11 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#newsletter"
            data-testid="hero-wishlist-button"
            className="bg-[#00F2FE] text-[#060A14] uppercase tracking-widest text-sm font-bold px-9 py-4 rounded-sm text-center hover:bg-white shadow-[0_0_28px_rgba(0,242,254,0.45)] transition-all duration-300"
          >
            {t("hero.wishlist")}
          </a>
          <a
            href="#world"
            data-testid="hero-explore-button"
            className="group glass text-white uppercase tracking-widest text-sm font-bold px-9 py-4 rounded-sm flex items-center justify-center gap-3 border border-white/15 hover:border-[#00F2FE] hover:text-[#00F2FE] transition-all"
          >
            {t("hero.explore")}
            <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform" />
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-6 right-6 z-10 hidden md:block text-right">
        <p className="text-[10px] tracking-[0.3em] text-slate-500">{t("hero.statusLabel")}</p>
        <p className="text-[#F9D423] text-xs tracking-[0.2em] flicker">{t("hero.statusValue")}</p>
      </div>
    </section>
  );
};

export default Hero;
